import React, { createContext, useState, useEffect } from 'react';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
    const [selectedDepo, setSelectedDepo] = useState('Global'); // 'Global' or depo ID
    const [depos, setDepos] = useState([]);

    // Auth State
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [systemSettings, setSystemSettings] = useState({});
    const [token, setToken] = useState(() => {
        const t = localStorage.getItem('erp_token');
        if (t === 'undefined' || t === 'null') return null;
        return t || null;
    });

    useEffect(() => {
        if (token && typeof token === 'string' && token.includes('.')) {
            console.log("Checking token:", token?.substring(0, 20) + "...");
            try {
                // Decode token to get user info 
                const parts = token.split('.');
                console.log("Token parts count:", parts.length);
                const payload = parts[0];

                if (!payload) throw new Error("Empty payload");

                // Handle URL-safe base64 and ensure proper padding
                let base64 = payload.trim().replace(/-/g, '+').replace(/_/g, '/');

                // Add padding if needed
                const pad = base64.length % 4;
                if (pad) {
                    if (pad === 1) throw new Error("Invalid base64 string length");
                    base64 += "=".repeat(4 - pad);
                }

                console.log("Raw payload part:", payload.substring(0, 20) + "...");
                console.log("Processed base64:", base64.substring(0, 20) + "...");

                // Decode and parse
                const decodedUser = JSON.parse(atob(base64));
                console.log("Token decoded successfully for user:", decodedUser.username);

                // Check expiry
                if (decodedUser.exp && decodedUser.exp * 1000 < Date.now()) {
                    console.warn("Token expired");
                    logout();
                } else {
                    setUser(decodedUser);
                }
            } catch (e) {
                console.error("Invalid token format or decoding failed:", e);
                // Only clear if definitely wrong and stored in localStorage
                if (token === localStorage.getItem('erp_token')) {
                    console.error("Corrupted token found in storage, clearing...");
                    logout();
                }
            }
        } else if (token) {
            console.warn("Malformed token detected:", token);
            logout();
        }
    }, [token]);

    const login = (newToken, userData) => {
        console.log("authFetch login called with token");
        localStorage.setItem('erp_token', newToken);
        setToken(newToken);
        setUser(userData);
    };

    const logout = () => {
        console.log("authFetch logout called");
        localStorage.removeItem('erp_token');
        setToken(null);
        setUser(null);
    };

    // Development vs production behavior:
    // * during `npm start` we use the create-react-app proxy (see package.json)
    //   so we keep BASE empty and let the dev server forward `/api` calls to
    //   the backend address. This avoids bypassing the proxy by sending
    //   absolute URLs.
    // * in other environments we fall back to the environment variable or
    //   a sensible default pointing at the Apache location where the backend
    //   lives under htdocs.
    const API_BASE = process.env.NODE_ENV === 'development'
        ? ''
        : '/backend'; // Force relative path for production

    // Helper fetch with Auth
    const authFetch = async (url, options = {}) => {
        // if caller passed a relative path, prepend base URL
        if (!url.startsWith('http')) {
            const base = API_BASE.replace(/\/$/, '');
            url = base + (url.startsWith('/') ? '' : '/') + url;
        }

        const headers = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(url, { ...options, headers });
        if (response.status === 401 || response.status === 403) {
            logout(); // Auto-logout if unauthorized
        }
        return response;
    };

    // Only fetch depos if authenticated. authFetch is a locally defined
    // helper; to avoid adding it to the dependency array (which would
    // cause unnecessary re-runs), we silence the rule here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (user) {
            authFetch('/api/depos')
                .then(res => res.json())
                .then(data => setDepos(Array.isArray(data) ? data : []))
                .catch(err => console.error("Error fetching depos:", err));

            authFetch('/api/settings')
                .then(res => res.json())
                .then(data => setSystemSettings(data))
                .catch(err => console.error("Error fetching settings:", err));
                
            if (user.role_id) {
                authFetch(`/api/roles/${user.role_id}`)
                    .then(res => {
                        if (res.ok) return res.json();
                        return [];
                    })
                    .then(data => setPermissions(Array.isArray(data) ? data.map(p => p.name) : []))
                    .catch(err => console.error("Error fetching permissions:", err));
            }
        }
    }, [user]);

    const hasPermission = (permName) => {
        if (user?.role === 'Admin' || user?.role_name === 'Super Admin') return true;
        return permissions.includes(permName);
    };

    return (
        <AppContext.Provider value={{
            selectedDepo, setSelectedDepo,
            depos, setDepos,
            systemSettings, setSystemSettings,
            permissions, hasPermission,
            user, token, login, logout, authFetch
        }}>
            {children}
        </AppContext.Provider>
    );
};
